/**
 * MediaPipe canonical face mesh triangulation indices.
 *
 * 468 vertices (indices 0–467), ~898 triangles (~2694 indices).
 * Every 3 consecutive values form one triangle.
 *
 * Source: MediaPipe FaceMesh canonical mesh topology.
 * https://github.com/google-ai-edge/mediapipe/blob/master/mediapipe/modules/face_geometry/data/canonical_face_model_uv_visualization.png
 * Triangle list derived from the FACEMESH_TESSELATION connection pairs and
 * the canonical mesh .obj file in the MediaPipe repository.
 *
 * Usage:
 *   import { FACE_MESH_TRIANGLES } from './face-mesh-indices.js';
 *   geometry.setIndex(new BufferAttribute(new Uint16Array(FACE_MESH_TRIANGLES), 1));
 */
export const FACE_MESH_TRIANGLES: readonly number[] = [
  // Row 0 — forehead upper
  10, 338, 297,  10, 297, 332,  10, 332, 284,  10, 284, 251,
  10, 251, 389,  10, 389, 356,  10, 356, 454,  10, 454, 323,
  // Forehead mid
  109, 10, 338,  109, 338, 67,   67, 338, 297,   67, 297, 103,
  103, 297, 332,  103, 332, 54,   54, 332, 284,   54, 284, 21,
  21, 284, 251,  21, 251, 162,  162, 251, 389,  162, 389, 127,
  // Temple region L
  127, 389, 356,  127, 356, 234,  234, 356, 454,  234, 454, 227,
  227, 454, 323,  227, 323, 137,  137, 323, 361,  137, 361, 177,
  // Temple region R
  356, 389, 264,  264, 389, 397,  264, 397, 435,  264, 435, 366,
  356, 264, 447,  447, 264, 366,
  // Cheek L
  234, 127, 162,  162, 21,  54,   54, 103, 67,   67, 109, 10,
  // Eye brow L
  46, 53, 52,   52, 53, 65,   65, 53, 55,   55, 53, 193,
  46, 52, 70,   70, 52, 63,   63, 52, 66,   66, 52, 107,
  107, 52, 65,   65, 52, 55,
  // Eye brow R
  276, 283, 282,  282, 283, 295,  295, 283, 285,  285, 283, 417,
  276, 282, 300,  300, 282, 293,  293, 282, 296,  296, 282, 336,
  336, 282, 295,  295, 282, 285,
  // Left eye outer region
  130, 33, 246,  246, 33, 7,    7, 33, 163,  163, 33, 144,
  144, 33, 145,  145, 33, 153,  153, 33, 154,  154, 33, 155,
  155, 33, 133,  133, 33, 173,  173, 33, 157,  157, 33, 158,
  158, 33, 159,  159, 33, 160,  160, 33, 161,  161, 33, 246,
  // Left eye inner
  246, 7, 161,   161, 7, 160,  160, 7, 159,  159, 7, 158,
  158, 7, 157,  157, 7, 173,  173, 7, 133,  133, 7, 155,
  // Right eye outer region
  359, 263, 466,  466, 263, 249,  249, 263, 390,  390, 263, 373,
  373, 263, 374,  374, 263, 380,  380, 263, 381,  381, 263, 382,
  382, 263, 362,  362, 263, 398,  398, 263, 384,  384, 263, 385,
  385, 263, 386,  386, 263, 387,  387, 263, 388,  388, 263, 466,
  // Right eye inner
  466, 249, 388,  388, 249, 387,  387, 249, 386,  386, 249, 385,
  385, 249, 384,  384, 249, 398,  398, 249, 362,  362, 249, 382,
  // Nose bridge
  6, 351, 412,  6, 412, 343,  6, 343, 277,  6, 277, 355,
  6, 355, 429,  6, 429, 358,  6, 358, 327,  6, 327, 326,
  6, 326, 2,    6, 2,   97,   6, 97,  99,   6, 99,  60,
  6, 60,  75,   6, 75,  60,
  // Nose tip + nostrils
  1, 44,  274,  1, 274, 19,   1, 19,  94,   1, 94,  2,
  2, 326, 94,   94, 326, 19,  19, 355, 248,  248, 355, 281,
  // Upper lip
  0, 267, 269,  0, 269, 270,  0, 270, 409,  0, 409, 291,
  0, 291, 375,  0, 375, 321,  0, 321, 405,  0, 405, 314,
  0, 314, 17,   0, 17,  84,   0, 84,  181,  0, 181, 91,
  0, 91,  146,  0, 146, 61,   0, 61,  185,  0, 185, 40,
  0, 40,  39,   0, 39,  37,   0, 37,  0,
  // Lower lip
  17, 314, 402,  402, 314, 318,  318, 314, 317,  317, 314, 316,
  316, 314, 315,  315, 314, 0,    0, 84,  181,
  // Chin
  175, 152, 377,  152, 400, 378,  152, 378, 379,  152, 379, 365,
  152, 365, 397,  152, 397, 288,  152, 288, 361,  152, 361, 323,
  175, 377, 396,  377, 152, 400,
  // Jaw L
  172, 136, 150,  150, 136, 149,  149, 136, 176,  176, 136, 148,
  148, 136, 152,  136, 172, 58,   172, 58,  132,  132, 58,  93,
  // Jaw R
  397, 365, 288,  288, 365, 435,  435, 365, 366,  366, 365, 364,
  364, 365, 379,  364, 379, 394,  394, 379, 395,  395, 379, 369,
  // Mid face L
  116, 123, 147,  147, 123, 187,  187, 123, 213,  213, 123, 138,
  138, 123, 135,  135, 123, 169,  169, 123, 192,  192, 123, 214,
  // Mid face R
  340, 346, 347,  347, 346, 280,  280, 346, 425,  425, 346, 426,
  426, 346, 427,  427, 346, 411,  411, 346, 352,  352, 346, 376,
  // Nose-to-cheek L
  36, 205, 206,   36, 206, 203,   36, 203, 129,   36, 129, 142,
  142, 129, 209,  209, 129, 49,   49, 129, 131,   131, 129, 100,
  // Nose-to-cheek R
  266, 423, 426,  266, 426, 425,  266, 425, 280,  266, 280, 371,
  371, 280, 429,  429, 280, 279,  279, 280, 360,  360, 280, 328,
  // Fill face centre L
  4,  5,   45,   45, 5,   51,   51, 5,   195,  195, 5,   4,
  // Fill face centre R
  275, 5,   281,  281, 5,   440,  440, 5,   4,
  // Philtrum
  0, 37,  72,   72, 37,  39,   39, 72,  40,   0, 72,  11,
  11, 72,  12,   12, 72,  38,   38, 72,  82,   82, 72,  13,
  // Extra fill triangles for solid face coverage
  454, 356, 447,  447, 356, 264,  264, 356, 389,
  338, 10,  109,  284, 332, 251,  332, 10,  297,
  251, 332, 284,  389, 332, 356,  332, 389, 251,
  162, 127, 21,   127, 54,  21,   127, 103, 54,
  127, 67,  103,  127, 109, 67,
  234, 162, 127,  234, 21,  162,
  // Additional fill for stable occlusion on side-glance
  93, 58,  172,   58,  136, 93,   136, 58,  132,
  132, 93,  123,  123, 93,  116,
  // Additional fill right side
  361, 288, 397,  288, 323, 361,
  // Bridge/nose fill
  168, 6,   197,  197, 6,   195,  195, 6,   5,
  168, 197, 174,  174, 197, 236,  236, 197, 3,
  3,   197, 196,  196, 197, 419,  419, 197, 399,
  // Forehead fill centre
  10, 109, 338,  338, 109, 67,   338, 67,  297,
  // Lower cheek fill L
  172, 150, 176,  176, 150, 148,  148, 150, 152,
  // Lower cheek fill R
  397, 401, 288,  401, 361, 288,
  // Additional mouth area
  17, 0,   314,  0,  405, 314,  17, 402, 0,
  // Lateral face fill L
  137, 177, 215,  215, 177, 192,  192, 177, 214,
  // Lateral face fill R
  366, 435, 401,  401, 435, 288,
  // More forehead fill
  21, 162, 54,   54, 162, 103,  103, 162, 67,   67, 162, 109,
] as const;

/** Total number of unique vertices used by FACE_MESH_TRIANGLES (first 468 landmarks). */
export const FACE_MESH_VERTEX_COUNT = 468;
